import { createSelector } from '@reduxjs/toolkit'
import type { NodeV2 } from '@vedalib/editor'
import { isNodeV2 } from '@vedalib/editor'
import * as R from 'ramda'

import { getParentNode } from 'components/Editor/EditorContext/selectors/block'
import { EMPTY_ARRAY } from 'constants/commonConstans'
import { INTERACTIVE_MODES } from 'services/Store/Project/constants'
import type { IProjectContext, Block } from 'services/Store/Project/types'
import { notEmpty } from 'utils/notEmpty'

import { getSection, getBlock, getNode, getBlockId } from '..'
import { DeviceMode } from '../../enums'

const getBlockWidth = (block: Block) =>
  R.pathOr<number | null>(
    null,
    ['schema', 'nodes', block.schema.rootId, 'style', DeviceMode.desktop, 'maxWidth'],
    block,
  )

export const getDeviceMode = ({ state }: IProjectContext) => state.deviceMode
export const getDeviceWidth = ({ state }: IProjectContext) => state.deviceWidth
export const getPreviewMode = ({ mode }: IProjectContext) => mode

export const getEditorMode = ({ state }: IProjectContext) => state.mode
export const getEditorHighlight = ({ state }: IProjectContext) => state.editor.highlight

export const getToolbar = ({ state }: IProjectContext) => state.editor.toolbar

export const getNavBar = ({ state }: IProjectContext) => state.editor.navBar

export const getIsInteractiveMode = ({ mode }: IProjectContext) => INTERACTIVE_MODES.includes(mode)
export const getSelectedNodeIds = ({ state }: IProjectContext) => state.editor.selectedNodes
export const getIsMultipleNodes = ({ state }: IProjectContext) =>
  state.editor.selectedNodes.length > 1
export const getMultipleNodes = createSelector(
  (state: IProjectContext) => getBlock(state),
  getSelectedNodeIds,
  (block, nodeIds) => nodeIds.map((id) => block && block.schema.nodes[id]).filter(isNodeV2),
)
export const isMultipleNodes = ({ state }: IProjectContext) => state.editor.selectedNodes.length > 1

export const getSelectedNodesByParent = createSelector(
  ({ state }: IProjectContext) => state.editor.selectedNodes,
  (context: IProjectContext, blockId: string | null) => getBlockId(context) === blockId,
  (context: IProjectContext, blockId: string | null) => getBlock(context, { id: blockId }),
  (context: IProjectContext, blockId: string | null, nodeId?: string | null) =>
    getNode(context, blockId, nodeId),
  (nodes, isCurrentBlock, block, parent) => {
    if (!isCurrentBlock) {
      return EMPTY_ARRAY
    }
    return nodes
      ?.map((id) => block && block.schema.nodes[id])
      .filter(isNodeV2)
      .filter((node) => node && parent && block && getParentNode(node.id, block)?.id === parent.id)
  },
)

export const getIsHighlightedSelectedNodesByParent = createSelector(
  ({ state }: IProjectContext) => state.editor.selectedNodes,
  (context: IProjectContext, blockId: string | null, nodeId?: string | null) =>
    getNode(context, blockId, nodeId),
  (nodes = EMPTY_ARRAY, parent) => {
    return parent?.children.some((nodeId) => nodes.includes(nodeId)) || false
  },
)

export const getIsMultipleNodesByParent = createSelector(
  ({ state }: IProjectContext) => state.editor.selectedNodes,
  (context: IProjectContext, blockId: string | null) => getBlock(context, { id: blockId }),
  (context: IProjectContext, blockId: string | null, nodeId?: string | null) =>
    getNode(context, blockId, nodeId),
  (nodes = EMPTY_ARRAY, block, parent) => {
    return (
      nodes
        ?.map((id) => block && block.schema.nodes[id])
        .filter(isNodeV2)
        .filter(
          (node) => node && parent && block && getParentNode(node.id, block)?.id === parent.id,
        ).length > 1
    )
  },
)

export const getIsMetaKey = ({ state }: IProjectContext) => state.editor.metaKey
export const getIsActiveNode = ({ state }: IProjectContext, nodeId: string) =>
  state.editor.selectedNodes.includes(nodeId) || false

export const getIsActiveChildNode = ({ state }: IProjectContext, children: string[]) =>
  children.some((id) => state.editor.selectedNodes.includes(id))
export const getIsHighlighted = createSelector(
  ({ state }: IProjectContext) => state.editor.highlight,
  (_state: IProjectContext, nodeId: string | null) => nodeId,
  (highlight, nodeId) => Boolean(nodeId && highlight === nodeId),
)
export const isBlockClosed = (state: IProjectContext) => {
  const section = getSection(state)
  const block = getBlock(state)
  return section?.isDone || block?.isDone
}

export const getCurrentBlockWidth = (state: IProjectContext) => {
  const block = getBlock(state)
  return block ? getBlockWidth(block) : null
}

export const getIsActiveBlock = ({ urlParams }: IProjectContext, id: string) =>
  urlParams.blockId === id
export const getSelectedBlocksCount = ({ state }: IProjectContext) =>
  state.editor.selectedBlocks.length

export const getSelectedBlocksIds = createSelector(
  ({ state }: IProjectContext) => state.editor.selectedBlocks.join(','),
  (state: IProjectContext) => getSection(state),
  (_state: IProjectContext, saveOrder?: boolean) => saveOrder,
  (selectedBlocks, section, saveOrder) =>
    saveOrder && section
      ? section.blocksOrder?.filter((id) => selectedBlocks.split(',').includes(id))
      : selectedBlocks.split(','),
)
export const getSelectedBlocks = createSelector(
  getSelectedBlocksIds,
  ({ data }: IProjectContext) => data.blocks,
  (ids, blocks) => ids.map((id) => blocks[id]).filter(notEmpty),
)
export const getIsSelectedBlock = ({ state }: IProjectContext, id: string) =>
  state.editor.selectedBlocks.includes(id)
export const isBlocksMultiSelect = (context: IProjectContext) => {
  return context.state.editor.selectedBlocks.length > 1
}

export const getIsLastSelected = ({ state }: IProjectContext, id: string) =>
  R.last(state.editor.selectedBlocks) === id
export const getSettings = ({ state }: IProjectContext) => state.editor.settings
export const getSyncing = ({ state }: IProjectContext) => state.editor.syncing

export const getIsActiveAltKey = ({ state }: IProjectContext) => state.editor.altKey
export const getIsActiveShiftKey = ({ state }: IProjectContext) => state.editor.shiftKey
export const getHighlighted = ({ state }: IProjectContext) => state.editor.highlight
export const getHighlightedNode = ({ state }: IProjectContext, node: NodeV2) =>
  state.editor.highlight && [node.id, ...node.children].includes(state.editor.highlight)
    ? state.editor.highlight
    : null

// export const getShowAltInfo = createSelector(
//   getIsActiveAltKey,
//   getEditorMode,
//   getHighlightedNode,
//   getActiveNodeId,
//   getBlock,
//   (altKey, editorMode, highlightNodeId, activeNodeId, block) => {
//     const parent = getParentNode(activeNodeId, block)

//     return (
//       altKey &&
//       editorMode === AppMode.pro &&
//       highlightNodeId &&
//       [parent?.id, ...(parent?.children || [])].includes(highlightNodeId)
//     )
//   },
// )
